import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n";

// Day: the earlier portraits on the white studio backdrop. Night: the newer portraits sent by the studio, on a black backdrop.
// (Different poses/crops, not just different backdrops, until white-backdrop originals of the new portraits are available.)
const PHOTOS_DAY = ["/founders/alessia-2026.jpg", "/founders/alessandra-2026.jpg"];
// Night: cleaned square cutouts (smoothed edges, no halo), pre-cropped for the circle. Widths are 1x / 2x / 3x of the 260px circle,
// never above the source resolution (Alessia's portrait is only 427px wide, so it stops at 2x).
const NIGHT = [
  { slug: "alessia", widths: [260, 520] },
  { slug: "alessandra", widths: [260, 520, 780] },
];
const nightSrcSet = ({ slug, widths }) => widths.map((w) => `/founders/${slug}-night-${w}.jpg ${w}w`).join(", ");
// Portrait sources cropped to a square circle: anchor near the top so the head is never cut (a centered crop clips the hair)
const PHOTO_POS = ["object-[50%_10%]", "object-[50%_10%]"];

export const Founders = () => {
  const { t } = useLanguage();
  return (
    <section id="fondatrici" data-testid="founders-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          className="max-w-2xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-sagedark mb-6">{t.founders.overline}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight text-ink">{t.founders.title}</h2>
        </motion.div>
        <div className="mt-16 grid md:grid-cols-2 gap-8">
          {t.founders.people.map((p, i) => (
            <motion.article
              key={p.name}
              data-testid={`founder-card-${p.name.split(" ")[0].toLowerCase()}`}
              className="group flex flex-col bg-white border border-ink/10 rounded-sm overflow-hidden shadow-[0_8px_30px_rgba(42,39,38,0.04)]"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
            >
              <div className="flex items-center justify-center pt-10 pb-6 px-8 founder-bg">
                <div className="relative w-full max-w-[260px] aspect-square shrink-0 rounded-full founder-bg founder-circle border-[3px] border-[#C5A059] overflow-hidden [transform:translateZ(0)]">
                  <img data-testid={`founder-photo-${p.name.split(" ")[0].toLowerCase()}`} src={PHOTOS_DAY[i]} alt={p.name} loading="lazy" className={`founder-img-day bw-hover w-full h-full rounded-full object-cover ${PHOTO_POS[i]}`} />
                  <img data-testid={`founder-photo-night-${p.name.split(" ")[0].toLowerCase()}`} src={`/founders/${NIGHT[i].slug}-night-${NIGHT[i].widths[1]}.jpg`} srcSet={nightSrcSet(NIGHT[i])} sizes="260px" alt={p.name} loading="lazy" decoding="async" className="founder-img-night bw-hover w-full h-full rounded-full object-cover" />
                </div>
              </div>
              <div className="p-8 flex flex-col justify-center">
                <h3 className="font-display text-3xl font-medium text-ink">{p.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] font-semibold text-amberdark">{p.role}</p>
                <p className="mt-5 text-sm leading-relaxed text-inksoft">{p.bio}</p>
                {p.linkedin && <a data-testid="founder-linkedin" href={p.linkedin} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C5A059] hover:text-ink">LinkedIn →</a>}
              </div>
            </motion.article>
          ))}
        </div>

      </div>
    </section>
  );
};
